import { Component, AfterViewInit, OnInit, ViewChild, ElementRef, Input, ChangeDetectorRef, forwardRef } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import IMask from 'imask';
import { takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/components/base.component';

@Component({
    selector: 'lex-edit-date-input',
    templateUrl: './edit-date-input.component.html',
    styleUrls: ['./edit-date-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditDateInputComponent),
            multi: true
        }
    ],
    standalone: false
})
export class EditDateInputComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('dateInput', { static: false }) dateInput!: ElementRef;
  @Input() formControl: any;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public _onChange = (value) => {
    // Intentionally Empty
  };
  public _onTouched = () => {
    // Intentionally Empty
  };
  public localDateString: string;
  private maskedInput: any;
  private initialDateString: string;

  constructor(
    private docViewerStore: DocViewerStore,
    private cd: ChangeDetectorRef
    ) {
    super();
  }

  public ngOnInit(): void {
    if (!this.formControl) {
      this.formControl = new FormControl<string>('');
    } else if (this.formControl.value) {
      this.initialDateString = this.formatDate(this.formControl.value);
    }

    this.docViewerStore.reset$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => {
      this.maskedInput.value = this.localDateString = this.initialDateString;
      this.formControl.reset(this.initialDateString)
    });
  }

  public ngAfterViewInit(): void {
    this.initializeMask();
  }

  private initializeMask() {
    if (this.dateInput && this.dateInput.nativeElement) {
      const maskOptions = {
        mask: Date,
        pattern: 'MM/DD/YYYY[ hh:mm:ss a]',
        overwrite: true,
        autofix: true,
        blocks: {
          YYYY: {
            mask: IMask.MaskedRange,
            from: 1900,
            to: 9999
          },
          MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12
          },
          DD: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31
          },
          hh: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12
          },
          mm: {
            mask: IMask.MaskedRange,
            from: 0,
            to: 59
          },
          ss: {
            mask: IMask.MaskedRange,
            from: 0,
            to: 59
          },
          a: {
            mask: IMask.MaskedEnum,
            enum: ['AM', 'PM']
          }
        },
        format: (date: Date) => {
          if (!date) return '';
          let hours = date.getHours();
          const minutes = date.getMinutes();
          const seconds = date.getSeconds();
          const period = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${period}`;
        },
        parse: (str: string) => {
          const [datePart, timePart, period] = str.split(' ');
          const [month, day, year] = datePart.split('/').map(Number);
          let hours = 0, minutes = 0, seconds = 0;
          if (timePart) {
            [hours, minutes, seconds] = timePart.split(':').map(Number);
            if (period && period.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (period && period.toUpperCase() === 'AM' && hours === 12) hours = 0;
          }
          return new Date(year, month - 1, day, hours, minutes, seconds);
        }
      };

      this.maskedInput = IMask(this.dateInput.nativeElement, maskOptions);

      if (this.formControl.value) {
        this.maskedInput.value = this.localDateString = this.formatDate(this.formControl.value);
        this.cd.detectChanges();
      }

      this.maskedInput.on('accept', () => {
        if (!this.maskedInput.unmaskedValue) {
          this.formControl.setValue('', { emitEvent: false });
          this._onChange('');
        } else if(this.isValidDate(this.maskedInput.value)){
          this.formControl.setValue(this.maskedInput.value, { emitEvent: false });
          this._onChange(this.maskedInput.value);
        }
      });

      this.maskedInput.on('complete', () => {
        if (!this.isValidDate(this.maskedInput.value))
          return;

        this.formControl.setValue(this.maskedInput.value, { emitEvent: false });
        this._onChange(this.maskedInput.value);
      });
    }
  }

  public writeValue(value: any): void {
		if (this.isValidDate(value) && this.formControl.value !== value) {
			this.formControl.setValue(value, { emit: false });
		}
	}

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  public onInputChange(value: any) {
    this._onChange(value);
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.dateInput && this.dateInput.nativeElement) {
      this.dateInput.nativeElement.disabled = isDisabled;
    }
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${period}`;
  }

  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }
}
